/**
 * QueryClient 配置（spec 08 §4.2）。
 *
 * 重试策略是这里最要紧的一条：**写操作一律不自动重试**。
 * 客户端虽然给每次写带了 Idempotency-Key，但重试语义应当由用户显式触发，
 * 静默重发一笔充值/上岗是我们不想解释的那类事故。
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (!(error instanceof ApiError)) return false;
        // 4xx 是我们自己请求得不对，重试多少次都一样
        if (!error.isTransient) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * queryKey 三段式：域 / 动作 / 参数（spec 08 §4.2，禁止字符串拼接）。
 * 集中在这里定义，写操作失效时才能精确 invalidate 而不是全量炸。
 */
export const qk = {
  me: () => ['auth', 'me'] as const,
  tenants: () => ['auth', 'tenants'] as const,

  employees: {
    list: (p: unknown) => ['employees', 'list', p] as const,
    detail: (id: string) => ['employees', 'detail', id] as const,
    dialPolicy: (id: string) => ['employees', 'dial-policy', id] as const,
    voice: (id: string) => ['employees', 'voice', id] as const,
    leadSchema: (id: string) => ['employees', 'lead-schema', id] as const,
    pushConfig: (id: string) => ['employees', 'push-config', id] as const,
    line: (id: string) => ['employees', 'line', id] as const,
    sms: (id: string) => ['employees', 'sms', id] as const,
    wechat: (id: string) => ['employees', 'wechat', id] as const,
    testRecords: (id: string, p: unknown) => ['employees', 'test-records', id, p] as const,
    script: (id: string) => ['employees', 'script', id] as const,
    scriptVersions: (id: string) => ['employees', 'script-versions', id] as const,
    scriptSlots: (id: string) => ['employees', 'script-slots', id] as const,
    memory: (id: string) => ['employees', 'memory', id] as const,
    memories: (id: string, p: unknown) => ['employees', 'memories', id, p] as const,
    share: (id: string) => ['employees', 'share', id] as const,
    hireJob: (id: string) => ['employees', 'hire-job', id] as const,

    stats: (id: string) => ['employees', 'stats', id] as const,
    statsBatch: (ids: string) => ['employees', 'stats-batch', ids] as const,
    dialProgress: (id: string, p: unknown) => ['employees', 'dial-progress', id, p] as const,
    dailyReport: (id: string, p: unknown) => ['employees', 'daily-report', id, p] as const,
    leadBatches: (id: string, p: unknown) => ['employees', 'lead-batches', id, p] as const,
    leads: (id: string, p: unknown) => ['employees', 'leads', id, p] as const,
    funnel: (id: string, p: unknown) => ['employees', 'funnel', id, p] as const,
    distribution: (id: string, p: unknown) => ['employees', 'distribution', id, p] as const,
    leadSource: (id: string) => ['employees', 'lead-source', id] as const,
    lineOptions: (id: string) => ['employees', 'line-options', id] as const,
  },
  lines: {
    withStats: (p: unknown) => ['lines', 'with-stats', p] as const,
  },
  reports: {
    daily: (p: unknown) => ['reports', 'daily', p] as const,
  },
  site: {
    public: () => ['site', 'public'] as const,
    page: () => ['site', 'page'] as const,
  },
  catalog: {
    blueprints: (p: unknown) => ['catalog', 'blueprints', p] as const,
    blueprint: (id: string) => ['catalog', 'blueprint', id] as const,
    industries: () => ['catalog', 'industries'] as const,
    channel: () => ['catalog', 'channel'] as const,
    capabilities: () => ['catalog', 'capabilities'] as const,
    voices: (p: unknown) => ['catalog', 'voices', p] as const,
  },
  resources: {
    lineApplications: (p: unknown) => ['resources', 'line-applications', p] as const,
    smsSignatures: () => ['resources', 'sms-signatures'] as const,
    smsTemplates: () => ['resources', 'sms-templates'] as const,
    wechatSeats: (p: unknown) => ['resources', 'wechat-seats', p] as const,
    blacklist: (p: unknown) => ['resources', 'blacklist', p] as const,
  },
  leads: {
    list: (p: unknown) => ['leads', 'list', p] as const,
    importJob: (id: string) => ['leads', 'import-job', id] as const,
  },
  campaigns: {
    list: (p: unknown) => ['campaigns', 'list', p] as const,
    detail: (id: string) => ['campaigns', 'detail', id] as const,
    progress: (id: string) => ['campaigns', 'progress', id] as const,
  },
  calls: {
    list: (p: unknown) => ['calls', 'list', p] as const,
    detail: (id: string) => ['calls', 'detail', id] as const,
    transcript: (id: string) => ['calls', 'transcript', id] as const,
  },
  dashboard: {
    funnel: (p: unknown) => ['dashboard', 'funnel', p] as const,
    distribution: (p: unknown) => ['dashboard', 'distribution', p] as const,
  },
  finance: {
    account: () => ['finance', 'account'] as const,
    flows: (p: unknown) => ['finance', 'flows', p] as const,
    recharge: (id: string) => ['finance', 'recharge', id] as const,
    consume: (p: unknown) => ['finance', 'consume', p] as const,
    seats: () => ['finance', 'seats'] as const,
    contracts: (p: unknown) => ['finance', 'contracts', p] as const,
    invoices: (p: unknown) => ['finance', 'invoices', p] as const,
  },
  tenant: {
    certification: () => ['tenant', 'certification'] as const,
  },
  system: {
    members: (p: unknown) => ['system', 'members', p] as const,
    roles: () => ['system', 'roles'] as const,
    apiKeys: (p: unknown) => ['system', 'api-keys', p] as const,
    logs: (p: unknown) => ['system', 'logs', p] as const,
    messages: (p: unknown) => ['system', 'messages', p] as const,
    unread: () => ['system', 'unread'] as const,
    lines: () => ['system', 'lines'] as const,
  },
} as const;
